import {
  AppstoreOutlined,
  ApiOutlined,
  AuditOutlined,
  CalendarOutlined,
  CarOutlined,
  FileDoneOutlined,
  DashboardOutlined,
  FileTextOutlined,
  ProfileOutlined,
  LogoutOutlined,
  SettingOutlined,
  ShopOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { PERMISSIONS } from '@navicosa/shared-types';
import { colors } from '@navicosa/ui-tokens';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { NotificationBell } from './NotificationBell';

const P = PERMISSIONS;

/** Menu theo PRD 6.1; mục chưa triển khai sẽ được thêm dần theo sprint. Ẩn mục không có quyền. */
const ITEMS: { key: string; label: string; icon: React.ReactNode; permission?: string }[] = [
  { key: '/', label: 'Tổng quan', icon: <DashboardOutlined /> },
  {
    key: '/requests',
    label: 'Yêu cầu thuê',
    icon: <FileTextOutlined />,
    permission: P.REQUESTS_READ,
  },
  {
    key: '/quotations',
    label: 'Báo giá',
    icon: <ProfileOutlined />,
    permission: P.QUOTATIONS_READ,
  },
  { key: '/bookings', label: 'Đơn thuê', icon: <FileDoneOutlined />, permission: P.BOOKINGS_READ },
  { key: '/schedule', label: 'Lịch xe', icon: <CalendarOutlined />, permission: P.EQUIPMENT_READ },
  { key: '/customers', label: 'Khách hàng', icon: <UserOutlined />, permission: P.CUSTOMERS_READ },
  {
    key: '/products',
    label: 'Sản phẩm & giá',
    icon: <AppstoreOutlined />,
    permission: P.CATALOG_READ,
  },
  {
    key: '/catalog',
    label: 'Danh mục & thông số',
    icon: <TagsOutlined />,
    permission: P.CATALOG_READ,
  },
  {
    key: '/equipment',
    label: 'Thiết bị thực tế',
    icon: <CarOutlined />,
    permission: P.EQUIPMENT_READ,
  },
  { key: '/suppliers', label: 'Đối tác', icon: <ShopOutlined />, permission: P.SUPPLIERS_READ },
  { key: '/users', label: 'Nhân viên', icon: <TeamOutlined />, permission: P.USERS_READ },
  {
    key: '/integrations/zalo-oa',
    label: 'Kết nối Zalo OA',
    icon: <ApiOutlined />,
    permission: P.SETTINGS_READ,
  },
  { key: '/settings', label: 'Cấu hình', icon: <SettingOutlined />, permission: P.SETTINGS_READ },
  { key: '/audit', label: 'Nhật ký hệ thống', icon: <AuditOutlined />, permission: P.AUDIT_READ },
];

export function AdminLayout() {
  const { me, can, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const items: MenuProps['items'] = ITEMS.filter((i) => !i.permission || can(i.permission)).map(
    (i) => ({
      key: i.key,
      label: i.label,
      icon: i.icon,
    }),
  );
  const selected = ITEMS.map((i) => i.key)
    .filter((k) => (k === '/' ? loc.pathname === '/' : loc.pathname.startsWith(k)))
    .slice(-1);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="light"
        width={232}
        style={{ borderRight: `1px solid ${colors.border}` }}
      >
        <div
          style={{
            padding: '18px 20px',
            fontWeight: 700,
            color: colors.brand600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {collapsed ? 'NV' : 'NAVICOSA RENTAL'}
        </div>
        <Menu
          mode="inline"
          items={items}
          selectedKeys={selected}
          onClick={(e) => nav(e.key)}
          style={{ borderInlineEnd: 0 }}
        />
      </Layout.Sider>
      <Layout>
        <Layout.Header
          style={{
            background: colors.surface,
            borderBottom: `1px solid ${colors.border}`,
            padding: '0 24px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {can(P.REQUESTS_READ) && <NotificationBell />}
          <Dropdown
            menu={{
              items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'Đăng xuất' }],
              onClick: () => void logout(),
            }}
          >
            <Button type="text">
              <Space>
                <Avatar size="small" style={{ background: colors.brand500 }}>
                  {me?.full_name.charAt(0)}
                </Avatar>
                <span>{me?.full_name}</span>
                <Typography.Text type="secondary">({me?.roles.join(', ')})</Typography.Text>
              </Space>
            </Button>
          </Dropdown>
        </Layout.Header>
        <Layout.Content style={{ padding: 24, maxWidth: 1400, width: '100%', margin: '0 auto' }}>
          <Outlet />
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
